import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { safeLocalStorage } from '@shared/safe-web-storage';

type ConsentState = 'granted' | 'denied';

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private initialized = false;
  private readonly consentKey = 'clavewinds_cookie_consent';

  constructor(
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  init() {
    if (!this.isBrowser || this.initialized || !environment.analyticsEnabled) return;
    this.initialized = true;

    this.ensureDataLayer();
    const savedConsent = this.getStoredConsent();
    this.setConsent(savedConsent ?? 'denied', false);
    this.loadGoogleTagManager();
    this.loadGoogleAnalytics();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const nav = event as NavigationEnd;
        this.trackPageView(nav.urlAfterRedirects);
      });
  }

  hasConsentDecision(): boolean {
    return this.getStoredConsent() !== null;
  }

  acceptCookies() {
    this.setConsent('granted', true);
  }

  rejectCookies() {
    this.setConsent('denied', true);
  }

  trackPageView(path: string) {
    this.push({
      event: 'page_view',
      page_path: path,
      page_location: this.document.location?.href,
      page_title: this.document.title
    });

    if (environment.googleAnalyticsMeasurementId && window.gtag) {
      window.gtag('config', environment.googleAnalyticsMeasurementId, {
        page_path: path,
        page_title: this.document.title
      });
    }
  }

  viewItem(product: any, model?: any) {
    this.pushEcommerceEvent('view_item', {
      currency: 'MXN',
      value: this.price(model?.Precio),
      items: [this.toItem(product, model, 1)]
    });
  }

  addToCart(product: any, model: any, quantity: number) {
    this.pushEcommerceEvent('add_to_cart', {
      currency: 'MXN',
      value: this.price(model?.Precio) * quantity,
      items: [this.toItem(product, model, quantity)]
    });
  }

  beginCheckout(items: any[], value: number) {
    this.pushEcommerceEvent('begin_checkout', {
      currency: 'MXN',
      value,
      items: this.toItems(items)
    });
  }

  purchase(orderId: string, items: any[], value: number, shipping = 0, paymentType?: string) {
    this.pushEcommerceEvent('purchase', {
      transaction_id: orderId,
      currency: 'MXN',
      value,
      shipping,
      payment_type: paymentType,
      items: this.toItems(items)
    });
  }

  private setConsent(state: ConsentState, persist: boolean) {
    this.ensureDataLayer();
    if (persist) safeLocalStorage.setItem(this.consentKey, state);
    window.gtag?.('consent', persist ? 'update' : 'default', {
      ad_storage: state,
      ad_user_data: state,
      ad_personalization: state,
      analytics_storage: state
    });
    this.push({ event: 'cookie_consent_update', consent_state: state });
  }

  private getStoredConsent(): ConsentState | null {
    if (!this.isBrowser) return null;
    const value = safeLocalStorage.getItem(this.consentKey);
    return value === 'granted' || value === 'denied' ? value : null;
  }

  private loadGoogleTagManager() {
    const id = environment.googleTagManagerId;
    if (!id || this.document.getElementById('gtm-script')) return;
    this.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    const script = this.document.createElement('script');
    script.id = 'gtm-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
    this.document.head.appendChild(script);
  }

  private loadGoogleAnalytics() {
    const id = environment.googleAnalyticsMeasurementId;
    if (!id || this.document.getElementById('ga4-script')) return;
    const script = this.document.createElement('script');
    script.id = 'ga4-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    this.document.head.appendChild(script);
    window.gtag?.('js', new Date());
    window.gtag?.('config', id, { send_page_view: false });
  }

  private pushEcommerceEvent(event: string, ecommerce: any) {
    this.push({ ecommerce: null });
    this.push({ event, ecommerce });
  }

  private push(payload: Record<string, any>) {
    if (!this.isBrowser || !environment.analyticsEnabled) return;
    this.ensureDataLayer();
    window.dataLayer?.push(payload);
  }

  private ensureDataLayer() {
    if (!this.isBrowser) return;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag(...args: any[]) {
      window.dataLayer?.push(args);
    };
  }

  private toItems(items: any[]): any[] {
    return (items || []).map((item) => this.toItem(item, item, Number(item.Cantidad || item.quantity || 1)));
  }

  private toItem(product: any, model: any = product, quantity = 1) {
    return {
      item_id: String(model?.IdProductosModelos ?? model?.IdProductoModelo ?? product?.IdProducto ?? product?.IdProd ?? ''),
      item_name: product?.Nombre ?? model?.Nombre ?? 'Producto',
      item_brand: product?.Marca ?? model?.Marca,
      item_variant: model?.Modelo,
      price: this.price(model?.Precio ?? product?.Precio),
      quantity
    };
  }

  private price(value: any): number {
    return Number(String(value ?? 0).replace(/[^\d.\-]/g, '')) || 0;
  }

  private get isBrowser() {
    return isPlatformBrowser(this.platformId);
  }
}
